import { useEffect, useRef, useState } from "react";
import { Button, Card, Input, makeStyles, tokens } from "@fluentui/react-components";
import { Broom16Regular, Send20Filled } from "@fluentui/react-icons";
import type { Window } from "@gymplan/shared";
import { api } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { ChatLog, type ChatMessage } from "./ChatLog.tsx";
import { answerAboutClients, looksLikeHours, type Knowledge } from "@gymplan/shared";

/** Which availability a change applies to. */
export type Target = "this" | "next" | "usual";

export interface Impact {
  client: string;
  weekday: string;
  startTime: string;
  endTime: string;
}

export interface Proposal {
  target: Target;
  slots: Window[];
  changes: string[];
  cellsChanged: number;
  /** The week already has bookings: saving needs the trainer's confirmation */
  booked?: boolean;
  /** Booked sessions that would fall outside the new hours */
  impacts?: Impact[];
}

type Interpretation = ({ ok: true } & Omit<Proposal, "target">) | { ok: false; clarify: string; chips: string[] };
const DAY: Record<string, string> = { MON: "Mon", TUE: "Tue", WED: "Wed", THU: "Thu", FRI: "Fri", SAT: "Sat", SUN: "Sun" };

const LABEL: Record<Target, string> = { this: "this week", next: "next week", usual: "your usual week" };
const SEEN_KEY = "rackup.availabilityChatSeen";
/** Set once the trainer has had the full introduction (per browser). */
const INTRO_KEY = "rackup.homeChatIntroduced";
const HELP = "I can help with two things here: changing your hours — like “add Sunday mornings next week” — and questions about your clients, like “who hasn't replied?” or “who's on Monday?”.";

const useStyles = makeStyles({
  card: { padding: 0, overflow: "hidden", marginBottom: "16px", gap: 0 },
  bar: { display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "4px 8px 0", minHeight: "32px" },
  composer: { display: "flex", flexDirection: "column", gap: "8px", padding: "10px 16px 14px", borderTop: `1px solid ${tokens.colorNeutralStroke2}` },
  chips: { display: "flex", flexWrap: "wrap", gap: "8px" },
  row: { display: "flex", gap: "8px" },
});

/** Pull "this week" / "next week" / "every week" out of what they typed. */
function targetOf(text: string): Target | null {
  const t = text.toLowerCase();
  if (/\bthis week\b/.test(t)) return "this";
  if (/\bnext week\b/.test(t)) return "next";
  if (/\b(every week|usual(ly)?|normal(ly)?|from now on|always|each week|going forward)\b/.test(t)) return "usual";
  return null;
}
const stripTarget = (t: string) => t.replace(/\b(for |from )?(this week|next week|every week|each week|from now on|going forward|usually|normally|always)\b/gi, " ").replace(/\s+/g, " ").trim();

/**
 * Change hours by chatting (#6), introduced like the setup chat. Shows the proposed change on the
 * matching week's grid (via onPreview) and saves only when the trainer taps Save.
 */
export function AvailabilityChat({ weeks, nextLocked, knowledge, onPreview, onSave }: { weeks: Record<"this" | "next", string>; /** next week is out to clients for replies */ nextLocked: boolean; /** what the chat can tell the trainer about their clients */ knowledge: Knowledge; onPreview: (p: Proposal | null) => void; onSave: (p: Proposal) => Promise<void> }) {
  const s = useStyles();
  const { user } = useAuth();
  const first = user?.name.split(" ")[0];
  const hi = first ? `Hi ${first} 👋` : "Hi 👋";
  // First time on the home page: say why the chat is here and what it does. After that, keep it short.
  const [firstVisit] = useState(() => {
    try {
      if (localStorage.getItem(INTRO_KEY)) return false;
      localStorage.setItem(INTRO_KEY, "1");
    } catch {
      /* private mode: short intro */
    }
    return true;
  });
  const intro: ChatMessage[] = firstVisit
    ? [
        { role: "bot", text: `${hi} I'm RackUp, your scheduling assistant. This is your home page — your hours and bookings for this week and next are below.` },
        { role: "bot", text: "Each week I message your clients on WhatsApp to find out when they can train, then build your schedule inside your hours. You check it and publish." },
        { role: "bot", text: "Use this chat to change your hours — like “add Sunday mornings next week” or “drop Friday evenings from now on” — and I'll show you the change before anything is saved." },
        { role: "bot", text: "You can also ask me about your clients: “who hasn't replied?”, “who's on Monday?”, or “when does Ann train?”." },
      ]
    : [
        { role: "bot", text: `${hi} Need to change something about next week, or want to know about a client?` },
        { role: "bot", text: "Tell me what's different — like “add Sunday mornings next week” — or ask “who hasn't replied?”. I'll show you any change before it's saved." },
      ];
  const [messages, setMessages] = useState<ChatMessage[]>(intro);
  const [log, setLog] = useState(0); // bumped on clear so the log redraws without retyping
  const [animateFrom, setAnimateFrom] = useState(() => {
    try {
      if (sessionStorage.getItem(SEEN_KEY)) return intro.length;
      sessionStorage.setItem(SEEN_KEY, "1");
    } catch {
      /* private mode */
    }
    return 0;
  });
  const [chips, setChips] = useState<string[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [settled, setSettled] = useState(false);
  const [awaitingTarget, setAwaitingTarget] = useState<string | null>(null);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => onPreview(proposal), [proposal, onPreview]);

  /** Back to the welcome: drops the conversation and any unsaved preview. Saved changes stay saved. */
  const clear = () => {
    setMessages(intro);
    setChips([]);
    setText("");
    setAwaitingTarget(null);
    setProposal(null);
    setAnimateFrom(intro.length);
    setLog((k) => k + 1);
    inputRef.current?.focus();
  };

  const say = (...bot: string[]) => setMessages((m) => [...m, ...bot.map((t) => ({ role: "bot" as const, text: t }))]);

  const interpret = async (change: string, target: Target) => {
    // Next week can't change while clients are answering against its hours: offer the usual week instead.
    if (target === "next" && nextLocked) {
      setAwaitingTarget(change);
      say("Next week is out to your clients right now, so it's locked until the schedule is built.", "Shall I change your usual week instead? That applies from the next week that isn't locked.");
      setChips(["Every week", "Cancel"]);
      return;
    }
    setBusy(true);
    try {
      const url = `/api/trainers/me/availability/interpret${target === "usual" ? "" : `?week=${weeks[target]}`}`;
      const r = await api.post<Interpretation>(url, { text: change });
      if (!r.ok) {
        say(r.clarify);
        setChips(r.chips);
        return;
      }
      if (r.cellsChanged === 0) {
        say(`That's already how ${LABEL[target]} looks — nothing to change.`);
        setChips([]);
        return;
      }
      setProposal({ target, slots: r.slots, changes: r.changes, cellsChanged: r.cellsChanged, booked: r.booked, impacts: r.impacts });
      const lines = [`Here's the change for ${LABEL[target]}:\n• ${r.changes.join("\n• ")}`];
      const impacts = r.impacts ?? [];
      if (impacts.length) {
        lines.push(`Heads up — ${impacts.length === 1 ? "a client is" : `${impacts.length} clients are`} already booked outside the new hours:\n• ${impacts.map((x) => `${x.client} — ${DAY[x.weekday] ?? x.weekday} ${x.startTime}`).join("\n• ")}`);
        lines.push("Their sessions stay booked — RackUp won't move or cancel them, so let them know if anything changes.");
        lines.push("I've shown the change on the grid. Save anyway?");
        setChips(["Save anyway", "Cancel"]);
      } else {
        if (r.booked) lines.push("Nobody already booked is affected.");
        lines.push("I've shown it on the grid (green = added, red = removed). Save it?");
        setChips(["Save", "Cancel"]);
      }
      say(...lines);
    } catch (e) {
      say(`Sorry — that didn't work (${(e as Error).message}). Try again?`);
    } finally {
      setBusy(false);
    }
  };

  const send = async (raw: string) => {
    const t = raw.trim();
    if (!t || busy) return;
    setMessages((m) => [...m, { role: "me", text: t }]);
    setText("");
    setChips([]);

    // Questions about clients are answered here, whatever else is going on; a pending change keeps its buttons.
    const isReply = /^(save( anyway)?|yes|y|ok|okay|do it|go|confirm|cancel|no|n|stop|never ?mind|leave it|this week|next week|every week)\b/i.test(t);
    if (!isReply) {
      const answer = answerAboutClients(t, knowledge);
      if (answer) {
        say(answer);
        if (proposal) setChips([proposal.booked ? "Save anyway" : "Save", "Cancel"]);
        else if (awaitingTarget) setChips(nextLocked ? ["This week", "Every week", "Cancel"] : ["Next week", "This week", "Every week"]);
        return;
      }
    }

    if (proposal) {
      if (/^(save|yes|y|ok|okay|do it|go|confirm)\b/i.test(t)) {
        setBusy(true);
        try {
          await onSave(proposal);
          say(`Done — ${LABEL[proposal.target]} is saved.${proposal.target === "usual" ? " Weeks you've changed by hand keep their own hours." : ""}`, "Anything else?");
          setProposal(null);
        } catch (e) {
          say(`That didn't save (${(e as Error).message}). Try again?`);
          setChips(["Save", "Cancel"]);
        } finally {
          setBusy(false);
        }
        return;
      }
      if (/^(cancel|no|n|stop|never ?mind|leave it)\b/i.test(t)) {
        setProposal(null);
        say("No problem — nothing's changed.");
        return;
      }
      setProposal(null); // they typed a new change instead
    }

    if (awaitingTarget && /^(cancel|no|n|never ?mind)\b/i.test(t)) {
      setAwaitingTarget(null);
      say("No problem — nothing's changed.");
      return;
    }
    if (awaitingTarget) {
      const target = targetOf(t) ?? (/^this\b/i.test(t) ? "this" : /^next\b/i.test(t) ? "next" : /^(every|usual)/i.test(t) ? "usual" : null);
      if (target) {
        const change = awaitingTarget;
        setAwaitingTarget(null);
        return interpret(change, target);
      }
      setAwaitingTarget(null); // treat as a new change
    }

    // Not a client question and not about hours: say what the chat is for instead of guessing.
    if (!looksLikeHours(t)) {
      setProposal(null);
      say(HELP);
      return;
    }

    const target = targetOf(t);
    const change = stripTarget(t);
    if (!target) {
      setAwaitingTarget(change);
      say(nextLocked ? "Next week is out to your clients right now. Is that for this week, or every week from now on?" : "Is that for next week, this week, or every week from now on?");
      setChips(nextLocked ? ["This week", "Every week", "Cancel"] : ["Next week", "This week", "Every week"]);
      return;
    }
    return interpret(change, target);
  };

  return (
    <Card className={s.card}>
      <div className={s.bar}>
        <Button size="small" appearance="subtle" icon={<Broom16Regular />} disabled={busy || messages.length <= intro.length} onClick={clear}>
          Clear chat
        </Button>
      </div>
      <ChatLog key={log} messages={messages} thinking={busy} animateFrom={animateFrom} onSettled={setSettled} compact />
      <div className={s.composer}>
        {settled && chips.length > 0 && (
          <div className={s.chips}>
            {chips.map((c) => (
              <Button key={c} shape="circular" size="small" appearance={c === "Save" ? "primary" : "secondary"} disabled={busy} onClick={() => void send(c)}>{c}</Button>
            ))}
          </div>
        )}
        <form className={s.row} onSubmit={(e) => { e.preventDefault(); void send(text); }}>
          <Input ref={inputRef} style={{ flex: 1 }} value={text} onChange={(_, d) => setText(d.value)} disabled={busy} placeholder="e.g. add Sunday mornings next week, or who hasn't replied?" aria-label="Tell RackUp what's changed" maxLength={300} />
          <Button type="submit" appearance="primary" icon={<Send20Filled />} disabled={!text.trim() || busy} aria-label="Send" />
        </form>
      </div>
    </Card>
  );
}
